import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Input, version2 } from 'ui-components/components/input';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import style from './html-editor-content.module.less';
import { SidebarCollapsedIcon } from './icons/sidebar-collapsed-icon';
import { SidebarExpandedIcon } from './icons/sidebar-expanded-icon';
import { messages } from './messages';
import { FormatButton } from '../format-button/format-button';
import { useLocalStorage } from '../../../../hooks/use-local-storage';
import { HTML_EDITOR_DARK_MODE, LAYOUTS, ORIENTATIONS } from '../../constants';
import { WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { SimpleDropdown } from '../../../simple-dropdown';
import { resolveApiError } from '../../api-validation-utils';
import { PreviewPanel } from '../../preview/preview-panel';
import { ErrorType, Orientation, SettingType } from '../../types';
import { useHtmlEditor } from '../../use-html-editor';
import { FieldReplacementSidebar } from '../field-replacement-sidebar/field-replacement-sidebar';
import { UndoRedoButtons } from '../undo-redo-buttons/undo-redo-buttons';
import { ValidationErrorBanner } from '../validation-error-banner/validation-error-banner';

import type {
  HtmlValidationError,
  Layout as LayoutType,
  Orientation as OrientationType,
  TemplateSchema,
  TemplateType,
  TrackWorkflowFunction,
} from '../../types';
import type { ChangeEvent, ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';

const orientationMessages: Record<OrientationType, MessageDescriptor> = {
  [Orientation.Portrait]: messages.orientationPortrait,
  [Orientation.Landscape]: messages.orientationLandscape,
};

const orientationItems = ORIENTATIONS.map((o) => ({
  key: String(o.value),
  name: <FormattedMessage {...orientationMessages[o.value]} />,
}));

const validateTemplateName = (value: string): boolean => {
  const minTemplateNameLength = 3;
  return value.trim().length < minTemplateNameLength;
};

type HtmlEditorContentProps = {
  schema: TemplateSchema;
  templateType: TemplateType;
  initialValue: string;
  initialTemplateName: string;
  initialLayout: LayoutType;
  initialOrientation: OrientationType;
  initialPreviewUrl: string;
  hasInitialFetchError: boolean;
  additionalFormFields?: ReactNode;
  allowedLayouts?: LayoutType[];
  sourceIds?: number[] | null;
  previewToolbar?: ReactNode;
  onCancel: () => void;
  onSave: (data: { name: string; html: string; layout: LayoutType; orientation: OrientationType }) => Promise<unknown>;
  onSuccessfulSave: () => void;
  tracking: TrackWorkflowFunction;
};

export const HtmlEditorContent = ({
  schema,
  templateType,
  initialValue,
  initialTemplateName,
  initialLayout,
  initialOrientation,
  initialPreviewUrl,
  hasInitialFetchError,
  additionalFormFields,
  allowedLayouts,
  sourceIds = null,
  previewToolbar,
  onCancel,
  onSave,
  onSuccessfulSave,
  tracking,
}: HtmlEditorContentProps) => {
  const intl = useIntl();

  const layoutItems = useMemo(
    () =>
      LAYOUTS.filter((size) => !allowedLayouts || allowedLayouts.includes(size.value)).map((size) => ({
        key: String(size.value),
        name: size.label,
      })),
    [allowedLayouts],
  );

  const { mutate: saveTemplateMutation, isPending: isSaving } = useMutation({
    mutationFn: onSave,
    onSuccess: (_, variables) => {
      onSuccessfulSave();
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.saveTemplateSuccessTitle} />,
        content: <FormattedMessage {...messages.saveTemplateSuccessContent} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
      tracking(WorkflowSteps.COMPLETE, {
        ...variables,
        html: null,
      });
      onCancel();
    },
    onError: (e) => {
      const apiErrorDetails = resolveApiError(e);
      validateAgainstApiError(apiErrorDetails);
      tracking(WorkflowSteps.ERROR, {
        type: ErrorType.Api,
        details: {
          error: e,
          layout,
          orientation,
          name: templateName,
        },
      });
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.saveTemplateErrorTitle} />,
        content: <FormattedMessage {...messages.saveTemplateErrorContent} />,
        dontHide: true,
      });
    },
  });

  const [isDarkMode, setIsDarkMode] = useLocalStorage(HTML_EDITOR_DARK_MODE, false);

  const handleDarkModeChange = useCallback(
    (checked: boolean) => {
      setIsDarkMode(checked);
      tracking(WorkflowSteps.CHANGE_SETTING, {
        settingType: SettingType.DarkMode,
        value: checked,
      });
    },
    [setIsDarkMode, tracking],
  );

  const [htmlValidationError, setHtmlValidationError] = useState<HtmlValidationError | null>(null);
  const [apiValidationError, setApiValidationError] = useState<HtmlValidationError | null>(null);

  const validationError = apiValidationError ?? htmlValidationError;

  const handleApiValidationChange = useCallback((error: HtmlValidationError | null) => {
    setApiValidationError(error);
    if (error) {
      setHtmlValidationError(null);
    }
  }, []);

  const {
    containerRef,
    insertField,
    getValue,
    validate,
    validateAgainstApiError,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    handleFormat,
    isFormatting,
    contentChange$,
  } = useHtmlEditor({
    initialValue,
    schema,
    isDarkMode,
    onValidationChange: setHtmlValidationError,
    onApiValidationChange: handleApiValidationChange,
  });

  const prevValidationError = useRef<string | null>(null);

  useEffect(() => {
    if (!validationError) {
      return;
    }
    // Create a unique key for the current validation error to avoid sending duplicate events for the same error
    // This is needed because the onValidationChange callback can be called multiple times with the same error, and we only want to track it once
    const errorUniqueKey = `${validationError.type}-${validationError.detail}-${validationError.line}`;
    if (prevValidationError.current === errorUniqueKey) {
      return;
    }

    prevValidationError.current = errorUniqueKey;

    tracking(WorkflowSteps.ERROR, {
      type: ErrorType.HtmlValidation,
      details: {
        errorType: validationError.type,
        errorDetail: validationError.detail,
        lineNumber: validationError.line,
      },
    });
  }, [getValue, tracking, validationError]);

  const trackAndFormat = useCallback(() => {
    const didFormat = handleFormat();
    if (didFormat) {
      tracking(WorkflowSteps.FORMAT_HTML);
    }
  }, [handleFormat, tracking]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        if (!isFormatting) {
          trackAndFormat();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFormatting, trackAndFormat]);

  const [templateName, setTemplateName] = useState(initialTemplateName);
  const [layout, setLayout] = useState<LayoutType>(initialLayout);
  const [orientation, setOrientation] = useState<OrientationType>(initialOrientation);
  const [hasTemplateNameError, setHasTemplateNameError] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (hasTemplateNameError) {
      tracking(WorkflowSteps.ERROR, {
        type: ErrorType.TemplateNameValidation,
        details: {
          name: templateName,
        },
      });
    }
    // TemplateName intentionally omitted from deps to avoid re-tracking on every keystroke
    // while error state persists. We only track when hasTemplateNameError transitions to true.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTemplateNameError, tracking]);

  const isDisabled = isSaving;

  const handleCancel = useCallback(() => {
    tracking(WorkflowSteps.CANCEL, {
      hasValidationError: !!validationError,
    });
    onCancel();
  }, [tracking, validationError, onCancel]);

  const handleLayoutChange = useCallback(
    (newLayout: string) => {
      const newLayoutValue = Number(newLayout) as LayoutType;
      tracking(WorkflowSteps.CHANGE_SETTING, {
        settingType: SettingType.Layout,
        fromValue: layout,
        toValue: newLayoutValue,
      });
      setLayout(newLayoutValue);
    },
    [layout, tracking],
  );

  const handleOrientationChange = useCallback(
    (newOrientation: string) => {
      const newOrientationValue = Number(newOrientation) as OrientationType;
      tracking(WorkflowSteps.CHANGE_SETTING, {
        settingType: SettingType.Orientation,
        fromValue: orientation,
        toValue: newOrientationValue,
      });
      setOrientation(newOrientationValue);
    },
    [orientation, tracking],
  );

  const handleTemplateNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTemplateName(value);
  }, []);

  const handleTemplateNameBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const hasError = validateTemplateName(value);
      setHasTemplateNameError(hasError);
      tracking(WorkflowSteps.CHANGE_SETTING, {
        settingType: SettingType.Name,
        value: value.trim(),
        hasValidationError: hasError,
      });
    },
    [tracking],
  );

  const onInsertField = useCallback(
    (fieldValue: string, isArrayField?: boolean) => {
      insertField(fieldValue, isArrayField);
      tracking(WorkflowSteps.INSERT_FIELD, {
        fieldValue,
      });
    },
    [insertField, tracking],
  );

  const onUndo = useCallback(() => {
    handleUndo();
    tracking(WorkflowSteps.UNDO);
  }, [handleUndo, tracking]);

  const onRedo = useCallback(() => {
    handleRedo();
    tracking(WorkflowSteps.REDO);
  }, [handleRedo, tracking]);

  const handleSave = useCallback(() => {
    const trimmedName = templateName?.trim() ?? '';
    if (validateTemplateName(trimmedName)) {
      setHasTemplateNameError(true);
      return;
    }

    const error = validate();
    if (error) {
      return;
    }

    saveTemplateMutation({
      name: trimmedName,
      html: getValue(),
      layout,
      orientation,
    });
  }, [templateName, validate, getValue, saveTemplateMutation, layout, orientation]);

  return (
    <>
      <FlexColumn className={classnames(style['drawer-content'], !isSidebarOpen && style['drawer-content-sidebar-collapsed'])}>
        <FlexRow className={style['form-row']} gap={Gap.Space16}>
          <FlexColumn className={style['form-field']} gap={Gap.Space4}>
            {/* @ts-expect-error - Text component accepts htmlFor for label elements */}
            <Text htmlFor='template-name-input' textType={TextType.Label} value={messages.templateNameLabel} />
            <Input
              id='template-name-input'
              inlineMessage={hasTemplateNameError ? intl.formatMessage(messages.templateNameMinLength) : undefined}
              inlineMessageType={hasTemplateNameError ? 'error' : undefined}
              onBlur={handleTemplateNameBlur}
              onChange={handleTemplateNameChange}
              value={templateName}
              version={version2}
            />
          </FlexColumn>
          <FlexColumn className={style['form-field']} gap={Gap.Space4}>
            <Text textType={TextType.Label} value={messages.pageSizeLabel} />
            <SimpleDropdown items={layoutItems} onItemSelected={handleLayoutChange} selectedItemKey={String(layout)} />
          </FlexColumn>
          <FlexColumn className={style['form-field']} gap={Gap.Space4}>
            <Text textType={TextType.Label} value={messages.orientationLabel} />
            <SimpleDropdown items={orientationItems} onItemSelected={handleOrientationChange} selectedItemKey={String(orientation)} />
          </FlexColumn>
          {additionalFormFields}
        </FlexRow>

        <FlexRow className={style.content}>
          <FlexColumn className={style['editor-section']}>
            <FlexRow className={style.toolbar} gap={Gap.Space8}>
              <FlexRow className={style['toolbar-left']} gap={isSidebarOpen ? undefined : Gap.Space8}>
                <FlexRow
                  className={classnames(style['insert-field-button-wrapper'], {
                    [style['insert-field-button-wrapper-expanded']]: isSidebarOpen,
                  })}
                >
                  <Button
                    ariaLabel={intl.formatMessage(isSidebarOpen ? messages.collapseSidebar : messages.expandSidebar)}
                    onClick={handleToggleSidebar}
                    theme={THEME_CONSTANTS.white}
                  >
                    <FlexRow className={style['insert-field-button-content']} gap={Gap.Space8}>
                      {isSidebarOpen ? <SidebarExpandedIcon /> : <SidebarCollapsedIcon />}
                      <FormattedMessage {...messages.insertFieldButton} />
                    </FlexRow>
                  </Button>
                </FlexRow>
                <FormatButton disabled={isFormatting || validationError !== null} isSidebarOpen={isSidebarOpen} onClick={trackAndFormat} />
              </FlexRow>
              <FlexRow className={style['toolbar-right']} gap={Gap.Space8}>
                <UndoRedoButtons canRedo={canRedo} canUndo={canUndo} isDisabled={isDisabled} onRedo={onRedo} onUndo={onUndo} />
                <CheckboxSlider
                  checkboxId='html-editor-dark-mode-toggle'
                  checked={isDarkMode}
                  className={style['dark-mode-toggle']}
                  labelText={<FormattedMessage {...messages.darkModeToggle} />}
                  onChange={handleDarkModeChange}
                />
              </FlexRow>
            </FlexRow>
            <div className={style['editor-body']}>
              {isSidebarOpen ? <FieldReplacementSidebar onFieldSelect={onInsertField} schema={schema} /> : null}
              <FlexColumn className={style['editor-area']} gap={Gap.Space16}>
                {validationError ? <ValidationErrorBanner error={validationError} /> : null}
                <div ref={containerRef} className={style['editor-container']} data-ignore-root-event-listeners />
              </FlexColumn>
            </div>
          </FlexColumn>

          <PreviewPanel
            contentChange$={contentChange$}
            hasInitialFetchError={hasInitialFetchError}
            hasValidationError={!!validationError}
            initialHtmlContent={initialValue}
            initialPreviewUrl={initialPreviewUrl}
            layout={layout}
            onApiValidationError={validateAgainstApiError}
            orientation={orientation}
            previewToolbar={previewToolbar}
            sourceIds={sourceIds}
            templateType={templateType}
            tracking={tracking}
          />
        </FlexRow>
      </FlexColumn>

      <FlexRow className={style.footer}>
        <Button disabled={isDisabled} onClick={handleCancel} theme={THEME_CONSTANTS.secondary}>
          <FormattedMessage {...messages.cancelButton} />
        </Button>
        {/* I don't want to block button when there is an API validation error, because then user can't click save which runs validation on the backend */}
        <Button disabled={!!htmlValidationError || hasTemplateNameError || isDisabled} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...messages.saveButton} />
        </Button>
      </FlexRow>
    </>
  );
};
